import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { readCallback, takeTransaction, type ReturnTo } from '../lib/oidc';
import { exchangeCode } from '../lib/session';

export type CallbackOutcome =
  | { ok: true; returnTo: ReturnTo }
  | { ok: false; message: string; returnTo?: ReturnTo };

// One exchange per page load: Trust's redirect is a full page load, React
// StrictMode runs effects twice in dev, and the stored transaction is
// single-use — so the first call's promise is shared.
let inflight: Promise<CallbackOutcome> | null = null;

function handleOnce(search: string): Promise<CallbackOutcome> {
  if (inflight) return inflight;
  inflight = (async (): Promise<CallbackOutcome> => {
    const r = readCallback(search, takeTransaction(window.sessionStorage));
    if (!r.ok) return { ok: false, message: r.message, returnTo: r.returnTo };
    try {
      await exchangeCode({ code: r.code, verifier: r.tx.verifier, redirectUri: r.tx.redirectUri, nonce: r.tx.nonce });
      return { ok: true, returnTo: r.tx.returnTo };
    } catch (e: any) {
      return { ok: false, message: `Sign-in failed: ${e?.message || e}`, returnTo: r.tx.returnTo };
    }
  })();
  return inflight;
}

/** /auth/callback — Trust sends the browser back here with ?code&state. */
export function AuthCallback({ onDone }: { onDone: (o: CallbackOutcome) => void }) {
  useEffect(() => {
    const search = window.location.search;
    // Keep the one-time code out of the address bar and history.
    if (search) window.history.replaceState(null, '', '/auth/callback');
    let cancelled = false;
    handleOnce(search).then((o) => { if (!cancelled) onDone(o); });
    return () => { cancelled = true; };
  }, [onDone]);

  return (
    <div className="min-h-screen flex items-center justify-center gap-3" style={{ color: 'rgba(240,253,244,0.72)' }}>
      <Loader2 className="animate-spin" size={18} />
      <span>Signing you in with Ordinal Pass…</span>
    </div>
  );
}
